/**
 * Boolean Field Type - Common
 *
 * Type-registry configuration, field-type configuration, and the shared
 * live bindings (getTypeConfig / resolveFieldConfig) used by the inline
 * (inline/boolean.tsx) and modal (modal/boolean.tsx) renderers.
 *
 * The `*Fn` bindings are ES-module live bindings: `initDependencies` calls the
 * setters, and the renderers (which import the bindings from this module)
 * observe the updated values.
 */

import type { FieldTypeConfig, TypeConfig } from '../../components/types';
import { S } from './columnWidths';
import { BooleanColors } from './columnColors';

// ============================================================================
// Type Registry Configuration
// ============================================================================

/**
 * Boolean type configuration for the type registry
 * Displays True/False for boolean values
 */
export const booleanTypeConfig: TypeConfig = {
  options: [
    { id: true, label: 'Yes' },
    { id: false, label: 'No' },
  ] as const,
  chipColors: BooleanColors,
};

// ============================================================================
// Shared live bindings (set lazily to avoid circular dependencies)
// ============================================================================

// Store reference to getTypeConfig to avoid circular dependencies (modal renderer)
// eslint-disable-next-line import/no-mutable-exports
export let getTypeConfigFn: ((type: string) => TypeConfig | undefined) | null = null;

/**
 * Set the getTypeConfig function (called from initDependencies to avoid circular deps)
 */
export const setGetTypeConfig = (fn: (type: string) => TypeConfig | undefined): void => {
  getTypeConfigFn = fn;
};

// Store reference to resolveFieldConfig to avoid circular dependencies (inline renderers)
// eslint-disable-next-line import/no-mutable-exports
export let resolveFieldConfigFn: ((col: { id: string; type?: string }) => unknown) | null = null;

/**
 * Set the resolveFieldConfig function (called from initDependencies to avoid circular deps)
 */
export const setResolveFieldConfig = (
  fn: (col: { id: string; type?: string }) => unknown
): void => {
  resolveFieldConfigFn = fn;
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Boolean field type configuration
 */
export const booleanFieldType: FieldTypeConfig = {
  placeholder: (label: string) => `Select ${label.toLowerCase()}...`,
  ...S,
};
